import React, { useState, useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { ActivQMSLoader } from "../ui/activqms-loader";
import {
  GET_LINKEDIN_OAUTH_STATUS,
  GET_LINKEDIN_CONVERSION_CONFIG,
  CREATE_LINKEDIN_CONVERSION_CONFIG,
  UPDATE_LINKEDIN_CONVERSION_CONFIG,
  DELETE_LINKEDIN_CONVERSION_CONFIG,
  GET_LINKEDIN_CONVERSION_STATS,
  isValidConversionUrn,
} from "../../services/linkedin-api";
import { useTenantContext } from "../../hooks/use-tenant-context";

interface LinkedInConversionConfigurationProps {
  conversationId: string;
  onConfigurationChange?: () => void;
}

export function LinkedInConversionConfiguration({
  conversationId,
  onConfigurationChange,
}: LinkedInConversionConfigurationProps) {
  const { tenantId } = useTenantContext();
  const [isEditing, setIsEditing] = useState(false);
  const [conversionUrn, setConversionUrn] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Check if LinkedIn is connected
  const { data: oauthData, loading: oauthLoading } = useQuery(
    GET_LINKEDIN_OAUTH_STATUS
  );

  // Get existing configuration
  const {
    data: configData,
    loading: configLoading,
    refetch: refetchConfig,
  } = useQuery(GET_LINKEDIN_CONVERSION_CONFIG, {
    variables: { conversationId },
    skip: !conversationId,
    fetchPolicy: "cache-and-network",
  });

  // Get conversion stats
  const { data: statsData } = useQuery(GET_LINKEDIN_CONVERSION_STATS, {
    variables: { conversationId },
    skip: !conversationId || !configData?.getLinkedInConversionConfig,
    fetchPolicy: "cache-and-network",
  });

  // Mutations
  const [createConfig, { loading: creating }] = useMutation(
    CREATE_LINKEDIN_CONVERSION_CONFIG,
    {
      onCompleted: () => {
        setSuccess("LinkedIn conversion tracking enabled successfully!");
        setIsEditing(false);
        refetchConfig();
        onConfigurationChange?.();
      },
      onError: (err) => {
        setError(`Failed to create configuration: ${err.message}`);
      },
    }
  );

  const [updateConfig, { loading: updating }] = useMutation(
    UPDATE_LINKEDIN_CONVERSION_CONFIG,
    {
      onCompleted: () => {
        setSuccess("LinkedIn conversion tracking updated successfully!");
        setIsEditing(false);
        refetchConfig();
        onConfigurationChange?.();
      },
      onError: (err) => {
        setError(`Failed to update configuration: ${err.message}`);
      },
    }
  );

  const [deleteConfig, { loading: deleting }] = useMutation(
    DELETE_LINKEDIN_CONVERSION_CONFIG,
    {
      onCompleted: () => {
        setSuccess("LinkedIn conversion tracking disabled successfully!");
        setConversionUrn("");
        setEnabled(false);
        refetchConfig();
        onConfigurationChange?.();
      },
      onError: (err) => {
        setError(`Failed to delete configuration: ${err.message}`);
      },
    }
  );

  // Load existing config into form
  useEffect(() => {
    if (configData?.getLinkedInConversionConfig) {
      const config = configData.getLinkedInConversionConfig;
      setConversionUrn(config.conversionUrn);
      setEnabled(config.enabled);
    }
  }, [configData]);

  const handleSave = () => {
    setError(null);
    setSuccess(null);

    // Validate conversion URN
    if (!conversionUrn.trim()) {
      setError("Conversion URN is required");
      return;
    }

    if (!isValidConversionUrn(conversionUrn)) {
      setError(
        "Invalid conversion URN format. Expected format: urn:lla:llaPartnerConversion:12345"
      );
      return;
    }

    if (configData?.getLinkedInConversionConfig) {
      // Update existing config
      const input = {
        conversationId,
        conversionUrn: conversionUrn.trim(),
        enabled,
      };
      updateConfig({ variables: { input } });
    } else {
      // Create new config - requires all fields
      const input = {
        conversationId,
        conversionUrn: conversionUrn.trim(),
        conversionName: "Destination Click Conversion",
        enabled,
        triggerOn: "DESTINATION_CLICK",
      };
      createConfig({ variables: { input } });
    }
  };

  const handleDelete = () => {
    if (
      window.confirm(
        "Are you sure you want to disable LinkedIn conversion tracking for this conversation?"
      )
    ) {
      setError(null);
      setSuccess(null);
      deleteConfig({ variables: { conversationId } });
    }
  };

  const isLinkedInConnected = oauthData?.getLinkedInOAuthStatus?.isConnected;
  const hasConfig = !!configData?.getLinkedInConversionConfig;
  const stats = statsData?.getLinkedInConversionStats;

  if (oauthLoading || configLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center">
          <ActivQMSLoader size="md" />
        </div>
      </div>
    );
  }

  if (!isLinkedInConnected) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start space-x-3">
          <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500 flex-shrink-0" />
          <div>
            <h4 className="text-md font-medium text-gray-900">
              LinkedIn Ads Not Connected
            </h4>
            <p className="mt-1 text-sm text-gray-600">
              Please connect your LinkedIn Ads account in Settings → Integrations before
              configuring conversion tracking.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="text-md font-medium text-gray-900">
            LinkedIn Conversion Tracking
          </h4>
          <p className="text-sm text-gray-600">
            Track destination clicks as conversions in LinkedIn Campaign Manager
          </p>
        </div>
        {hasConfig && !isEditing && (
          <div className="flex items-center space-x-2">
            {enabled ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <CheckCircleIcon className="h-4 w-4 mr-1" />
                Enabled
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                Disabled
              </span>
            )}
          </div>
        )}
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-800">{success}</p>
        </div>
      )}

      {/* Configuration Form */}
      {(isEditing || !hasConfig) && (
        <div className="space-y-4 mb-4">
          <div>
            <label
              htmlFor="conversionUrn"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Conversion URN
            </label>
            <input
              type="text"
              id="conversionUrn"
              value={conversionUrn}
              onChange={(e) => setConversionUrn(e.target.value)}
              placeholder="urn:lla:llaPartnerConversion:12345"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
            />
            <p className="mt-1 text-xs text-gray-500">
              Find this in LinkedIn Campaign Manager → Conversion Tracking
            </p>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="enabled"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="enabled" className="ml-2 block text-sm text-gray-900">
              Enable conversion tracking
            </label>
          </div>
        </div>
      )}

      {/* Display Mode */}
      {hasConfig && !isEditing && (
        <div className="space-y-2 mb-4 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Conversion URN:</span>
            <span className="font-mono text-xs text-gray-900">{conversionUrn}</span>
          </div>
        </div>
      )}

      {/* Conversion Stats */}
      {stats && hasConfig && !isEditing && (
        <div className="mb-4 p-4 bg-gray-50 rounded-md">
          <h5 className="text-sm font-medium text-gray-900 mb-2">Conversion Stats</h5>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Total Events:</span>
              <span className="ml-2 font-medium text-gray-900">
                {stats.totalEvents}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Conversion Rate:</span>
              <span className="ml-2 font-medium text-gray-900">
                {stats.conversionRate.toFixed(1)}%
              </span>
            </div>
            <div>
              <span className="text-gray-600">Successful:</span>
              <span className="ml-2 font-medium text-green-600">
                {stats.successfulEvents}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Failed:</span>
              <span className="ml-2 font-medium text-red-600">
                {stats.failedEvents}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center space-x-3">
        {isEditing || !hasConfig ? (
          <>
            <button
              onClick={handleSave}
              disabled={creating || updating}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating || updating ? (
                <>
                  <ActivQMSLoader size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                "Save Configuration"
              )}
            </button>
            {hasConfig && (
              <button
                onClick={() => setIsEditing(false)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
            )}
          </>
        ) : (
          <>
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Edit Configuration
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deleting ? (
                <>
                  <ActivQMSLoader size="sm" className="mr-2" />
                  Deleting...
                </>
              ) : (
                "Disable Tracking"
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

